import type {
  EventAnalysisJob,
  EventAnalysisJobResult,
  EventAnalysisWorkerRequest,
  EventAnalysisWorkerResponse,
  EventDetailsJob,
  EventDetailsJobResult,
  EventModelJob,
  EventModelJobResult
} from './event-analysis-computation';
import type { AnalysisSeries } from './analysis';

const MAX_CACHE_ENTRIES = 32;
const seriesTokens = new WeakMap<AnalysisSeries, number>();
const resultCache = new Map<string, Promise<EventAnalysisJobResult>>();
let nextSeriesToken = 1;
let nextRequestId = 1;
let worker: Worker | null | undefined;
let workerFailed = false;
const pending = new Map<number, {
  resolve: (result: EventAnalysisJobResult) => void;
  reject: (reason: Error) => void;
}>();

function seriesToken(series: AnalysisSeries): number {
  const existing = seriesTokens.get(series);
  if (existing != null) return existing;
  const token = nextSeriesToken++;
  seriesTokens.set(series, token);
  return token;
}

function seriesKey(series: AnalysisSeries[]): string {
  return series.map(seriesToken).join(',');
}

export function eventAnalysisJobCacheKey(job: EventAnalysisJob): string {
  if (job.kind === 'details') {
    return `details|selected:${seriesKey(job.selectedSeries)}|statistics:${seriesKey(job.statisticSeries)}`;
  }
  const groupKey = job.groups
    .map((group) => `${seriesToken(group.representative)}:${seriesKey(group.allSeries)}`)
    .join(';');
  return `model|${job.mode}|selected:${seriesKey(job.selectedSeries)}|groups:${groupKey}`;
}

function failWorker(reason: Error): void {
  workerFailed = true;
  worker?.terminate();
  worker = null;
  for (const request of pending.values()) request.reject(reason);
  pending.clear();
}

function analysisWorker(): Worker | null {
  if (worker !== undefined) return worker;
  worker = null;
  if (workerFailed || typeof Worker === 'undefined') return null;
  try {
    worker = new Worker(new URL('./event-analysis.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }: MessageEvent<EventAnalysisWorkerResponse>) => {
      const request = pending.get(data.requestId);
      if (!request) return;
      pending.delete(data.requestId);
      if ('error' in data) request.reject(new Error(data.error));
      else request.resolve(data.result);
    };
    worker.onerror = () => failWorker(new Error('The event-analysis worker stopped unexpectedly.'));
  } catch {
    workerFailed = true;
    worker = null;
  }
  return worker;
}

async function synchronousFallback(job: EventAnalysisJob): Promise<EventAnalysisJobResult> {
  const { computeEventAnalysisJob } = await import('./event-analysis-computation');
  return computeEventAnalysisJob(job);
}

function execute(job: EventAnalysisJob): Promise<EventAnalysisJobResult> {
  const activeWorker = analysisWorker();
  if (!activeWorker) return synchronousFallback(job);
  return new Promise<EventAnalysisJobResult>((resolve, reject) => {
    const requestId = nextRequestId++;
    pending.set(requestId, { resolve, reject });
    try {
      const message: EventAnalysisWorkerRequest = { requestId, job };
      activeWorker.postMessage(message);
    } catch (reason) {
      pending.delete(requestId);
      reject(reason instanceof Error ? reason : new Error('The event-analysis request could not be sent.'));
    }
  }).catch(() => synchronousFallback(job));
}

function retain(cacheKey: string, result: Promise<EventAnalysisJobResult>): void {
  resultCache.delete(cacheKey);
  resultCache.set(cacheKey, result);
  while (resultCache.size > MAX_CACHE_ENTRIES) {
    const oldest = resultCache.keys().next().value as string | undefined;
    if (oldest == null) break;
    resultCache.delete(oldest);
  }
}

export function runEventAnalysisJob(job: EventModelJob): Promise<EventModelJobResult>;
export function runEventAnalysisJob(job: EventDetailsJob): Promise<EventDetailsJobResult>;
export function runEventAnalysisJob(job: EventAnalysisJob): Promise<EventAnalysisJobResult>;
export function runEventAnalysisJob(job: EventAnalysisJob): Promise<EventAnalysisJobResult> {
  const cacheKey = eventAnalysisJobCacheKey(job);
  const cached = resultCache.get(cacheKey);
  if (cached) {
    retain(cacheKey, cached);
    return cached;
  }
  const result = execute(job);
  retain(cacheKey, result);
  void result.catch(() => {
    if (resultCache.get(cacheKey) === result) resultCache.delete(cacheKey);
  });
  return result;
}
